import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import Checkbox from '@/common/components/Checkbox'
import GoBackButton from '@/common/components/GoBackButton'
import MaskedTextField from '@/common/components/MaskedTextField'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import TextField from '@/common/components/TextField'
import useDebounce from '@/common/hooks/useDebounce'
import { useGoBack } from '@/common/hooks/useGoBack'
import { PermissionData } from '@/common/types/permissions'
import { FormTransformers } from '@/common/utils/formTransformers'
import { getRailsUpdate } from '@/common/utils/getRailsUpdate'
import { mapToDeletion } from '@/common/utils/railsHelpers'
import { usePermissions } from '@/features/tcardRoles/hooks/usePermissions'
import useRole from '@/features/tcardRoles/hooks/useRole'
import useUpdateRole from '@/features/tcardRoles/hooks/useUpdateRole'
import { roleSchema, RoleValidationSchema } from '@/features/tcardRoles/schemas/create.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { BadgeHelpIcon, ChevronsRightIcon, SearchIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'

const Update = () => {
  const { id } = useParams()
  const goBack = useGoBack()

  const containerRef = useRef<HTMLDivElement>(null)

  const [search, setSearch] = useDebounce('')

  const [selectedPermissions, setSelectedPermissions] = useState<{ permission_id: string }[]>([])
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { data: initialRole, isLoading, isFetched: initialRoleIsFetched } = useRole({ id: id || '' })

  const {
    data: permissions,
    meta,
    isFetching: permissionsIsFetching,
    fetchNextPage,
  } = usePermissions<PermissionData[]>({
    filters: { search },
  })

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<RoleValidationSchema>({
    resolver: zodResolver(roleSchema),
  })

  const { mutate: updateRole, isPending: updateRoleIsPending } = useUpdateRole({
    roleId: id || '',
    permissions: getRailsUpdate(
      initialRole?.permission_roles || [],
      selectedPermissions,
      (value) => value.permission.id,
      (value) => value.permission_id,
      mapToDeletion,
      (value) => ({ ...value }),
    ),
    onSuccess: () => {
      goBack({ fallback: `/tcard-roles/${id}` })
    },
    onError: FormTransformers.errorsTransformer<RoleValidationSchema>({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<RoleValidationSchema> = (data) => updateRole(data)

  const isToFetchNextPage = () => {
    if (!containerRef.current || meta.current_page >= meta.total_pages || permissionsIsFetching) {
      return false
    }
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current
    return scrollTop + clientHeight >= scrollHeight - 16
  }

  const handleScroll = () => {
    if (isToFetchNextPage()) {
      fetchNextPage()
    }
  }

  useEffect(() => {
    if (initialRoleIsFetched) {
      setSelectedPermissions(
        initialRole?.permission_roles.map((permission) => ({ permission_id: permission.permission.id })) || [],
      )
    }
  }, [initialRoleIsFetched])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2 items-center">
          <GoBackButton fallback={`/tcard-roles/${id}`} />
          <p className="text-white-400 font-normal">Cargos e permissões - Tcard</p>
          <ChevronsRightIcon color="#ffffffb3" />
          {!isLoading && <h2 className="font-bold">{initialRole?.display_name}</h2>}
        </div>

        <div className="flex gap-4 items-center">
          <Button
            type="button"
            variant="outline-error"
            onClick={() => goBack({ fallback: `/tcard-roles/${id}` })}
            disabled={updateRoleIsPending}
          >
            Cancelar
          </Button>

          <Button
            type="submit"
            name='submit'
            className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
            disabled={isLoading || updateRoleIsPending}
            isLoading={updateRoleIsPending}
          >
            Confirmar
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Box>Carregando...</Box>
      ) : (
        <Box className="flex flex-col gap-y-6 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] max-h-[calc(100vh-128px)] overflow-y-auto">
          <div className="flex flex-col gap-y-4">
            <p className="text-lg font-bold">Cargo</p>

            <TextField
              label="Nome do cargo"
              markAsRequired
              maxLength={64}
              defaultValue={initialRole?.display_name}
              errorMessage={errors.display_name?.message || undefined}
              {...register('display_name')}
              disabled={updateRoleIsPending}
            />

            <TextField
              label="Descrição do cargo"
              markAsRequired
              maxLength={256}
              defaultValue={initialRole?.description}
              errorMessage={errors.description?.message || undefined}
              {...register('description')}
              disabled={updateRoleIsPending}
            />

            <MaskedTextField
              mask={'00'}
              label='Nível'
              placeholder='0'
              defaultValue={initialRole?.level || ''}
              errorMessage={errors.level?.message || undefined}
              {...register('level')}
              disabled={updateRoleIsPending}
            />

            <Checkbox
              label="Permitir visualizar dados de empresas que não está vinculado como Responsável Interno ou Substituto do Responsável Interno"
              className="items-center gap-x-2"
              labelClassName="text-[0.625rem] font-light italic"
              defaultChecked={initialRole?.can_bypass_linked_client_company_restriction}
              errorMessage={errors.can_bypass_linked_client_company_restriction?.message || undefined}
              {...register('can_bypass_linked_client_company_restriction')}
              disabled={updateRoleIsPending}
            />

            <Checkbox
              label="Permitir visualizar dados de estabelecimentos que não está vinculado como Responsável Interno ou Substituto do Responsável Interno"
              className="items-center gap-x-2"
              labelClassName="text-[0.625rem] font-light italic"
              defaultChecked={initialRole?.can_bypass_linked_establishment_company_restriction}
              errorMessage={errors.can_bypass_linked_establishment_company_restriction?.message || undefined}
              {...register('can_bypass_linked_establishment_company_restriction')}
              disabled={updateRoleIsPending}
            />
          </div>

          <Box className="flex flex-col gap-y-6">
            <div className="flex flex-col gap-y-4">
              <p>Permissões</p>

              <TextField
                LeftIcon={<SearchIcon />}
                placeholder="Pesquisar permissões"
                onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
                disabled={updateRoleIsPending}
              />
            </div>

            <div
              ref={containerRef}
              onScroll={handleScroll}
              className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2 overflow-auto min-h-14 max-h-[calc(100vh-566px)]"
              data-testid="permission_update_list"
            >
              {permissions?.map((permission) => (
                <div key={permission.id} className="flex gap-2 items-center">
                  <Checkbox
                    name={permission.display_name}
                    label={permission.display_name}
                    labelClassName="max-w-[360px] text-wrap break-words"
                    checked={selectedPermissions.find((value) => value.permission_id === permission.id) ? true : false}
                    onChange={(e) => {
                      if ((e.target as HTMLInputElement).checked) {
                        setSelectedPermissions([...selectedPermissions, { permission_id: permission.id }])
                      } else {
                        setSelectedPermissions(
                          selectedPermissions.filter((value) => value.permission_id !== permission.id),
                        )
                      }
                    }}
                    disabled={updateRoleIsPending}
                  />

                  <div data-tooltip-id={permission.id} className="flex gap-1 items-center text-warning">
                    <BadgeHelpIcon strokeWidth={1} size={14} />

                    <p className="font-light text-[8px] italic">Informações</p>
                  </div>

                  <Tooltip id={permission.id}>{permission.description}</Tooltip>
                </div>
              ))}
            </div>
          </Box>
        </Box>
      )}

      <ModalMessageSimple messages={messages} setMessages={setMessages} isError />
    </form>
  )
}

export default Update
